import { Fingerprint, KeyRound, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { ApiError, api } from "../api";
import { useAuth } from "../auth";
import { Button, Logo } from "../components";
import "./onboarding.css";

export function AuthPage({ mode, inviteToken, claimToken, returnTo }: { mode: "signin" | "signup"; inviteToken?: string; claimToken?: string; returnTo?: string }) {
  const { user, register, signIn, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  if (user && !inviteToken && !claimToken) return <Navigate to={returnTo ?? "/app"} replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "signup") {
        await register({ email, displayName, organizationName: inviteToken || claimToken ? undefined : organizationName.trim() || undefined, inviteToken, claimToken });
      } else {
        await signIn(email);
      }
      if (claimToken && mode === "signin") {
        const result = await api<{ organizationId: string }>("/api/app/claim", { method: "POST", body: JSON.stringify({ token: claimToken }) });
        localStorage.setItem("lg.organization", result.organizationId);
        sessionStorage.removeItem("lg.pendingClaim");
        await refresh();
      }
      // A connector consent page passes returnTo directly; a protected route passes `from` in router state.
      const from = returnTo ?? (location.state as { from?: string } | null)?.from;
      navigate(inviteToken && mode === "signin" ? `/join/${inviteToken}` : claimToken ? "/app" : from ?? (mode === "signup" && !inviteToken ? "/app/onboarding" : "/app"), { replace: true });
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : caught instanceof Error ? caught.message : "Passkey setup did not finish";
      setError(message.includes("ceremony was cancelled") ? "The passkey prompt was cancelled. Try again when you’re ready." : message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`auth-layout journey-auth ${claimToken ? "journey-auth--claim" : inviteToken ? "journey-auth--invite" : ""}`}>
      <section className="auth-story">
        <Link to="/" aria-label="LoopGroup home"><Logo /></Link>
        <div className="auth-story__center">
          <span className="journey-state-chip is-current">{claimToken ? "Stage 03 · Claim identity" : inviteToken ? "Invitation · Not joined" : mode === "signin" ? "Identity · Returning" : "Identity · New workspace"}</span>
          <p className="eyebrow">{claimToken ? "Claim your catalog" : inviteToken ? "Group invitation" : "Private by default"}</p>
          <h1>{claimToken ? "Put your name on the map." : mode === "signin" ? "Return to your loops." : inviteToken ? "Step into the group." : "Map the work your agents already do."}</h1>
          <p>{claimToken ? "The loops and report-only connection already exist. Your passkey claims them without installing an observer or editing a loop." : mode === "signin" ? "Use the passkey on this device to return to your loops." : "No password. Your passkey opens a private loop catalog; groups stay separate until you choose what to share."}</p>
          <div className="auth-loop-visual"><i /><i /><i /><span>people</span><span>agents</span><b>group</b></div>
        </div>
        <div className="auth-story__ledger"><span><b>Already happened</b>{claimToken ? "Private catalog received" : inviteToken ? "Invitation issued" : "Nothing outside this form"}</span><span><b>Not happened</b>{claimToken ? "No observer, reporting, or sharing" : inviteToken ? "No group access yet" : "No loops shared"}</span></div>
        <small>Private by default · Scoped agent access · Auditable consent</small>
      </section>
      <main className="auth-panel">
        <div className="auth-card">
          <div className="auth-icon"><Fingerprint size={24} /></div>
          <p className="eyebrow">{claimToken ? "Claim temporary catalog" : mode === "signin" ? "Welcome back" : inviteToken ? "Group invitation" : "Create your private workspace"}</p>
          <h2>{mode === "signin" ? "Sign in with a passkey" : claimToken ? "Create a passkey and claim" : "Set up your LoopGroup"}</h2>
          <p className="auth-card__intro">{mode === "signin" ? "Enter the email attached to your passkey. The device will ask you to approve the sign-in." : "A passkey ties the account to Face ID, Touch ID, Windows Hello, or your security key."}</p>
          <form onSubmit={submit}>
            {mode === "signup" && (
              <label><span>Your name</span><input autoComplete="name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} placeholder="Ada Lovelace" required minLength={2} /></label>
            )}
            <label><span>Email</span><input type="email" autoComplete="email webauthn" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="ada@company.com" required /></label>
            {mode === "signup" && !inviteToken && !claimToken && (
              <label><span>Workspace name <small>optional</small></span><input value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} placeholder="Ada’s loops" minLength={2} /></label>
            )}
            {error && <div className="form-error journey-inline-error" role="alert"><strong>Passkey step did not finish.</strong><span>{error}</span><small>No claim or invitation state changed.</small></div>}
            <Button type="submit" busy={busy} className="button--full"><KeyRound size={16} /> {claimToken ? (mode === "signin" ? "Sign in and claim my loops" : "Create passkey and claim my loops") : mode === "signin" ? "Continue with passkey" : "Create passkey and map my loops"}</Button>
          </form>
          {mode === "signup" && <p className="auth-legal">By creating an account you agree to the <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>}
          <div className="auth-safe"><ShieldCheck size={16} /><span><b>Device boundary</b>Your biometric never leaves your device. LoopGroup stores only the public credential.</span></div>
          <p className="auth-switch">{mode === "signin" ? <>New to LoopGroup? <Link to={claimToken ? `/claim/signup#${claimToken}` : inviteToken ? `/join/${inviteToken}` : "/signup"}>Create an account</Link></> : <>Already have an account? <Link to={claimToken ? `/claim/signin#${claimToken}` : inviteToken ? `/join/${inviteToken}?signin=1` : "/signin"}>Sign in</Link></>}</p>
        </div>
      </main>
    </div>
  );
}

export function JoinPage() {
  const { token = "" } = useParams();
  const { user, loading, refresh } = useAuth();
  const [status, setStatus] = useState<"idle" | "accepting" | "error">("idle");
  const [message, setMessage] = useState("");
  const navigate = useNavigate();
  const signInExisting = new URLSearchParams(window.location.search).get("signin") === "1";

  useEffect(() => {
    if (!user || loading || status !== "idle") return;
    setStatus("accepting");
    void api<{ organizationId: string }>("/api/app/invitations/accept", { method: "POST", body: JSON.stringify({ token }) })
      .then(async (result) => {
        await refresh();
        localStorage.setItem("lg.organization", result.organizationId);
        window.location.assign("/app");
      })
      .catch((error: unknown) => {
        setStatus("error");
        setMessage(error instanceof Error ? error.message : "The invitation could not be accepted");
      });
  }, [user, loading, status, token, refresh, navigate]);

  if (loading) return <div className="boot journey-boot" role="status"><div className="boot__loop" /><strong>Opening invitation</strong><span>Checking identity and group access.</span></div>;
  if (!user) return <AuthPage mode={signInExisting ? "signin" : "signup"} inviteToken={token} />;
  return (
    <div className={`join-state journey-join ${status === "error" ? "is-failure" : "is-loading"}`}><Logo /><span className={`journey-state-chip ${status === "error" ? "is-failure" : "is-current"}`}>{status === "error" ? "Invitation not accepted" : "Invitation acceptance in progress"}</span><div className="boot__loop" /><h1>{status === "error" ? "Invitation unavailable" : "Joining the group…"}</h1><p>{message || "We’re attaching this account to the invited group. No private loops are shared by joining."}</p><small>{status === "error" ? "No membership change was made." : "Next: the shared operating picture will open after acceptance."}</small>{status === "error" && <Button onClick={() => navigate("/app")}>Return to your groups</Button>}</div>
  );
}
